/**
 * Delivery Coaching Service
 * Provides real-time feedback on speaking pace, filler words, and delivery quality
 * Uses Web Speech Recognition API for real-time transcription
 */

export type CoachingStatus = 'idle' | 'awaiting' | 'listening' | 'processing' | 'networkError';

export interface CoachingMetrics {
  currentPace: number; // WPM
  paceStatus: 'good' | 'too-fast' | 'too-slow';
  fillerWordCount: number;
  totalWords: number;
  suggestions: string[];
  transcript: string;
  status: CoachingStatus;
}

// Phase 2 Sprint 17 Spec: Filler words by language
const FILLER_WORDS_BY_LANG: Record<string, string[]> = {
  'en': ['um', 'uh', 'like', 'you know', 'so', 'actually', 'basically', 'literally'],
  'es': ['eh', 'um', 'este', 'o sea', 'pues', 'bueno', 'como'],
  'fr': ['euh', 'ben', 'genre', 'en fait', 'donc', 'voilà'],
  'de': ['äh', 'ähm', 'also', 'halt', 'sozusagen', 'quasi'],
  'pt': ['é', 'tipo', 'então', 'né', 'assim', 'sabe'],
  'it': ['cioè', 'tipo', 'praticamente', 'insomma', 'ecco'],
  'nl': ['eh', 'nou', 'dus', 'eigenlijk', 'zeg maar'],
  'zh': ['那个', '就是', '然后', '嗯'],
  'ja': ['えーと', 'あの', 'まあ', 'なんか'],
  'ko': ['음', '그', '뭐', '저기'],
};

// Default English filler words
const FILLER_WORDS = FILLER_WORDS_BY_LANG['en'];

// Phase 2 Sprint 17 Spec: WPM range 120-180
const IDEAL_WPM_MIN = 120;
const IDEAL_WPM_MAX = 180;

/**
 * Delivery Coaching Service for real-time speech analysis
 */
// Supported languages for speech recognition
export const SUPPORTED_LANGUAGES = [
  { code: 'en-US', name: 'English (US)', short: 'en' },
  { code: 'en-GB', name: 'English (UK)', short: 'en' },
  { code: 'es-ES', name: 'Spanish', short: 'es' },
  { code: 'fr-FR', name: 'French', short: 'fr' },
  { code: 'de-DE', name: 'German', short: 'de' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)', short: 'pt' },
  { code: 'it-IT', name: 'Italian', short: 'it' },
  { code: 'nl-NL', name: 'Dutch', short: 'nl' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', short: 'zh' },
  { code: 'ja-JP', name: 'Japanese', short: 'ja' },
  { code: 'ko-KR', name: 'Korean', short: 'ko' },
];

export class DeliveryCoachingService {
  private recognition: any = null;
  private startTime: number = 0;
  private wordCount: number = 0;
  private fillerCount: number = 0;
  private transcript: string = '';
  private onUpdate: ((metrics: CoachingMetrics) => void) | null = null;
  private isRunning: boolean = false;
  private shouldRestart: boolean = false;
  private currentStatus: CoachingStatus = 'idle';
  private isPaused: boolean = false;
  private lastActionTime: number = 0;
  private currentLang: string = 'en-US';
  private currentFillerWords: string[] = FILLER_WORDS;

  constructor() {
    // Initialize Web Speech Recognition if available
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        this.recognition = new SpeechRecognition();
        this.recognition.continuous = true;
        this.recognition.interimResults = true;
        this.recognition.lang = this.currentLang;
        this.recognition.maxAlternatives = 1;
      }
    }
  }

  /**
   * Set the language for speech recognition
   */
  setLanguage(langCode: string): void {
    this.currentLang = langCode;
    const shortCode = langCode.split('-')[0];
    this.currentFillerWords = FILLER_WORDS_BY_LANG[shortCode] || FILLER_WORDS;
    
    if (this.recognition) {
      this.recognition.lang = langCode;
    }
    console.log(`[DeliveryCoaching] Language set to: ${langCode}`);
  }

  /**
   * Get current language
   */
  getLanguage(): string {
    return this.currentLang;
  }

  /**
   * Check if speech recognition is supported
   */
  isSupported(): boolean {
    return this.recognition !== null;
  }

  /**
   * Start delivery coaching with microphone permission
   */
  async start(onUpdate: (metrics: CoachingMetrics) => void): Promise<void> {
    if (!this.isSupported()) {
      console.warn('[DeliveryCoaching] Speech Recognition not supported');
      throw new Error('Speech Recognition not supported in this browser');
    }

    // Prevent multiple starts
    if (this.isRunning) {
      console.log('[DeliveryCoaching] Already running, updating callback only');
      this.onUpdate = onUpdate;
      return;
    }

    // Request microphone permission explicitly
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('[DeliveryCoaching] Microphone permission granted');
    } catch (error) {
      console.error('[DeliveryCoaching] Microphone permission denied:', error);
      throw new Error('Microphone permission required for delivery coaching');
    }

    this.onUpdate = onUpdate;
    this.startTime = Date.now();
    this.wordCount = 0;
    this.fillerCount = 0;
    this.transcript = '';
    this.isRunning = true;
    this.shouldRestart = true;
    this.currentStatus = 'listening';

    this.setupRecognitionHandlers();
    
    try {
      this.recognition.start();
      console.log('[DeliveryCoaching] Speech recognition started');
      
      // Emit initial listening state
      this.emitUpdate();
    } catch (e: any) {
      if (e.message?.includes('already started')) {
        console.log('[DeliveryCoaching] Recognition already active');
      } else {
        throw e;
      }
    }
  }

  private emitUpdate(): void {
    if (!this.onUpdate) return;
    
    const elapsedMinutes = (Date.now() - this.startTime) / 60000;
    const currentPace = elapsedMinutes > 0.1 ? Math.round(this.wordCount / elapsedMinutes) : 0;
    
    let paceStatus: 'good' | 'too-fast' | 'too-slow' = 'good';
    if (currentPace > 0) {
      if (currentPace < IDEAL_WPM_MIN) {
        paceStatus = 'too-slow';
      } else if (currentPace > IDEAL_WPM_MAX) {
        paceStatus = 'too-fast';
      }
    }

    this.onUpdate({
      currentPace,
      paceStatus,
      fillerWordCount: this.fillerCount,
      totalWords: this.wordCount,
      suggestions: this.generateSuggestions(paceStatus, this.fillerCount, this.wordCount),
      transcript: this.transcript,
      status: this.currentStatus
    });
  }

  private setupRecognitionHandlers(): void {
    this.recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      // Process final results for accurate metrics
      if (finalTranscript) {
        console.log('[DeliveryCoaching] Final transcript:', finalTranscript.trim());
        this.currentStatus = 'processing';
        this.transcript += finalTranscript;
        this.analyzeTranscript(finalTranscript);
      }
      
      // Also update with interim results for real-time feedback
      if (interimTranscript && !finalTranscript) {
        // Calculate approximate metrics from interim + existing
        this.currentStatus = 'processing';
        this.updateMetricsWithInterim(interimTranscript);
      }
    };

    this.recognition.onerror = (event: any) => {
      console.error('[DeliveryCoaching] Recognition error:', event.error);
      
      // Handle specific errors
      if (event.error === 'not-allowed') {
        this.shouldRestart = false;
        this.currentStatus = 'idle';
        this.emitUpdate();
      } else if (event.error === 'network') {
        // Network error - Chrome's Speech API requires internet
        console.log('[DeliveryCoaching] Network error - Speech API requires internet connection');
        this.currentStatus = 'networkError';
        this.emitUpdate();
        // Will auto-restart via onend handler
      } else if (event.error === 'no-speech') {
        // No speech detected - this is normal, just continue listening
        console.log('[DeliveryCoaching] No speech detected, continuing...');
        this.currentStatus = 'listening';
        this.emitUpdate();
      } else if (event.error === 'aborted') {
        // Recognition was aborted - may need restart
        console.log('[DeliveryCoaching] Recognition aborted');
      }
    };

    this.recognition.onend = () => {
      console.log('[DeliveryCoaching] Recognition ended, shouldRestart:', this.shouldRestart);
      
      // Auto-restart if we should still be running
      if (this.shouldRestart && this.isRunning) {
        console.log('[DeliveryCoaching] Auto-restarting speech recognition...');
        setTimeout(() => {
          if (this.shouldRestart && this.isRunning) {
            try {
              this.recognition.start();
              console.log('[DeliveryCoaching] Speech recognition restarted');
              // Reset status to listening after successful restart
              this.currentStatus = 'listening';
              this.emitUpdate();
            } catch (e) {
              console.error('[DeliveryCoaching] Failed to restart:', e);
            }
          }
        }, 100);
      }
    };

    this.recognition.onspeechstart = () => {
      console.log('[DeliveryCoaching] Speech detected');
      this.currentStatus = 'processing';
      this.emitUpdate();
    };

    this.recognition.onspeechend = () => {
      console.log('[DeliveryCoaching] Speech ended');
      this.currentStatus = 'listening';
      this.emitUpdate();
    };
  }

  /**
   * Update metrics with interim transcript for real-time feedback
   */
  private updateMetricsWithInterim(interimText: string): void {
    const interimWords = interimText.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    const totalWordsWithInterim = this.wordCount + interimWords.length;
    
    // Calculate WPM including interim words
    const elapsedMinutes = (Date.now() - this.startTime) / 60000;
    const currentPace = elapsedMinutes > 0.1 ? Math.round(totalWordsWithInterim / elapsedMinutes) : 0;

    // Determine pace status
    let paceStatus: 'good' | 'too-fast' | 'too-slow' = 'good';
    if (currentPace > 0) {
      if (currentPace < IDEAL_WPM_MIN) {
        paceStatus = 'too-slow';
      } else if (currentPace > IDEAL_WPM_MAX) {
        paceStatus = 'too-fast';
      }
    }

    // Count interim filler words
    const interimFillerCount = this.countFillerWords(interimText.toLowerCase());

    // Update with combined metrics - keep status as 'processing' during interim
    // since user is actively speaking
    if (this.onUpdate) {
      this.onUpdate({
        currentPace,
        paceStatus,
        fillerWordCount: this.fillerCount + interimFillerCount,
        totalWords: totalWordsWithInterim,
        suggestions: this.generateSuggestions(paceStatus, this.fillerCount + interimFillerCount, totalWordsWithInterim),
        transcript: this.transcript + interimText,
        status: 'processing'
      });
    }
  }

  /**
   * Pause delivery coaching (preserves metrics)
   */
  pause(): void {
    // Debounce rapid calls
    const now = Date.now();
    if (now - this.lastActionTime < 100) return;
    this.lastActionTime = now;
    
    if (this.isPaused) return; // Already paused
    
    console.log('[DeliveryCoaching] Pausing speech recognition');
    this.isPaused = true;
    this.shouldRestart = false;
    this.currentStatus = 'awaiting';
    
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (e) {
        // Ignore errors when stopping
      }
    }
    
    this.emitUpdate();
  }

  /**
   * Resume delivery coaching (preserves metrics)
   */
  resume(): void {
    // Debounce rapid calls
    const now = Date.now();
    if (now - this.lastActionTime < 100) return;
    this.lastActionTime = now;
    
    if (!this.isRunning || !this.recognition) return;
    if (!this.isPaused) return; // Already running
    
    console.log('[DeliveryCoaching] Resuming speech recognition');
    this.isPaused = false;
    this.shouldRestart = true;
    this.currentStatus = 'listening';
    
    try {
      this.recognition.start();
      console.log('[DeliveryCoaching] Speech recognition resumed');
      this.emitUpdate();
    } catch (e: any) {
      if (e.message?.includes('already started')) {
        console.log('[DeliveryCoaching] Recognition already active');
        this.emitUpdate();
      } else {
        console.error('[DeliveryCoaching] Failed to resume:', e);
      }
    }
  }

  /**
   * Stop delivery coaching
   */
  stop(): void {
    if (!this.isRunning) return; // Already stopped
    
    console.log('[DeliveryCoaching] Stopping speech recognition');
    this.isRunning = false;
    this.shouldRestart = false;
    this.currentStatus = 'idle';
    this.isPaused = false;
    
    if (this.recognition) {
      try {
        this.recognition.stop();
      } catch (e) {
        // Ignore errors when stopping
      }
    }
  }

  /**
   * Analyze transcript for filler words and pace
   */
  private analyzeTranscript(text: string): void {
    const words = text.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    this.wordCount += words.length;

    // Count filler words
    const newFillerCount = this.countFillerWords(text.toLowerCase());
    this.fillerCount += newFillerCount;

    // Calculate WPM
    const elapsedMinutes = (Date.now() - this.startTime) / 60000;
    const currentPace = elapsedMinutes > 0.1 ? Math.round(this.wordCount / elapsedMinutes) : 0;

    // Determine pace status
    let paceStatus: 'good' | 'too-fast' | 'too-slow' = 'good';
    if (currentPace > 0) {
      if (currentPace < IDEAL_WPM_MIN) {
        paceStatus = 'too-slow';
      } else if (currentPace > IDEAL_WPM_MAX) {
        paceStatus = 'too-fast';
      }
    }

    // Generate suggestions
    const suggestions = this.generateSuggestions(paceStatus, this.fillerCount, this.wordCount);

    console.log('[DeliveryCoaching] Metrics updated:', {
      wordCount: this.wordCount,
      currentPace,
      paceStatus,
      fillerCount: this.fillerCount
    });

    // Return to listening after processing
    this.currentStatus = 'listening';

    // Update metrics
    if (this.onUpdate) {
      this.onUpdate({
        currentPace,
        paceStatus,
        fillerWordCount: this.fillerCount,
        totalWords: this.wordCount,
        suggestions,
        transcript: this.transcript,
        status: this.currentStatus
      });
    }
  }

  /**
   * Count filler words in text
   */
  private countFillerWords(text: string): number {
    let count = 0;
    this.currentFillerWords.forEach(filler => {
      // Use word boundary for languages that support it, or simple includes for CJK
      const isCJK = /[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff\uac00-\ud7af]/.test(filler);
      if (isCJK) {
        // For CJK languages, count occurrences directly
        const parts = text.split(filler);
        count += parts.length - 1;
      } else {
        const regex = new RegExp(`\\b${filler}\\b`, 'gi');
        const matches = text.match(regex);
        if (matches) {
          count += matches.length;
        }
      }
    });
    return count;
  }

  /**
   * Generate coaching suggestions
   */
  private generateSuggestions(
    paceStatus: 'good' | 'too-fast' | 'too-slow',
    fillerCount: number,
    totalWords: number
  ): string[] {
    const suggestions: string[] = [];

    // Pace suggestions
    if (paceStatus === 'too-fast') {
      suggestions.push('Slow down - take a breath between sentences');
      suggestions.push('Pause for emphasis on key points');
    } else if (paceStatus === 'too-slow') {
      suggestions.push('Speak a bit faster to maintain energy');
      suggestions.push('Keep the momentum going');
    } else if (totalWords > 0) {
      suggestions.push('Great pace - keep it up!');
    }

    // Filler word suggestions
    const fillerRate = totalWords > 0 ? (fillerCount / totalWords) * 100 : 0;
    if (fillerRate > 5) {
      suggestions.push('Try to reduce filler words - pause instead');
      suggestions.push('Take a breath when you need to think');
    } else if (fillerRate < 2 && totalWords > 10) {
      suggestions.push('Excellent - minimal filler words!');
    }

    return suggestions.slice(0, 3); // Return top 3 suggestions
  }

  /**
   * Get current metrics without starting recognition
   */
  getCurrentMetrics(): CoachingMetrics {
    const elapsedMinutes = (Date.now() - this.startTime) / 60000;
    const currentPace = elapsedMinutes > 0 ? Math.round(this.wordCount / elapsedMinutes) : 0;
    
    let paceStatus: 'good' | 'too-fast' | 'too-slow' = 'good';
    if (currentPace < IDEAL_WPM_MIN) {
      paceStatus = 'too-slow';
    } else if (currentPace > IDEAL_WPM_MAX) {
      paceStatus = 'too-fast';
    }

    return {
      currentPace,
      paceStatus,
      fillerWordCount: this.fillerCount,
      totalWords: this.wordCount,
      suggestions: this.generateSuggestions(paceStatus, this.fillerCount, this.wordCount),
      transcript: this.transcript,
      status: this.currentStatus
    };
  }

  /**
   * Reset metrics
   */
  reset(): void {
    this.startTime = Date.now();
    this.wordCount = 0;
    this.fillerCount = 0;
    this.transcript = '';
  }
}

// Singleton instance
let coachingServiceInstance: DeliveryCoachingService | null = null;

export const getDeliveryCoachingService = (): DeliveryCoachingService => {
  if (!coachingServiceInstance) {
    coachingServiceInstance = new DeliveryCoachingService();
  }
  return coachingServiceInstance;
};
